import React, { useState } from 'react';
import { Search, AlertCircle, CheckCircle, XCircle, Shield, FileText, Award, Zap, Users, Clock, Lock, TrendingUp, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import PopupButton from '../components/PopupButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import TestimonialSlider from '../components/TestimonialSlider';
import HowItWorksSlider from '../components/HowItWorksSlider';
import WhyYouNeedItSlider from '../components/WhyYouNeedItSlider';
import WhyChooseUsSlider from '../components/WhyChooseUsSlider';
import BenefitsSlider from '../components/BenefitsSlider';
import { trademarkSearchResults, trademarkSearchPricing, searchTestimonials } from '../data/mock';
import SEO from '../components/SEO';

const TrademarkSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    
    setTimeout(() => {
      const result = trademarkSearchResults.find(
        item => item.name.toLowerCase() === searchQuery.toLowerCase()
      ) || {
        name: searchQuery,
        status: 'Available',
        risk: 'Low',
        category: 'General',
        description: 'No conflicts found in USPTO database. This trademark appears to be available for registration.'
      };
      
      setSearchResult(result);
      setIsSearching(false);
    }, 1500);
  };

  const getStatusIcon = (status) => {
    if (status === 'Available') return <CheckCircle className="w-8 h-8 text-green-600" />;
    if (status === 'Not Available') return <XCircle className="w-8 h-8 text-red-600" />;
    return <AlertCircle className="w-8 h-8 text-yellow-600" />;
  };

  const getRiskColor = (risk) => {
    if (risk === 'Low') return 'text-green-600 bg-green-50';
    if (risk === 'High') return 'text-red-600 bg-red-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const processSteps = [
    {
      step: 1,
      title: 'Submit Your Brand Name',
      description: 'Enter your desired trademark name and business information for the search.',
      icon: FileText
    },
    {
      step: 2,
      title: 'Comprehensive Search',
      description: 'We search USPTO database, state registrations, and common law trademarks.',
      icon: Search
    },
    {
      step: 3,
      title: 'Receive Detailed Report',
      description: 'Get a complete analysis with conflict assessment and recommendations.',
      icon: Award
    }
  ];

  const whyYouNeedIt = [
    {
      icon: Lock,
      title: 'Avoid Rejections',
      description: 'Identify potential conflicts before filing to save time and money.'
    },
    {
      icon: Shield,
      title: 'Reduce Legal Risk',
      description: 'Prevent infringement issues and costly legal disputes down the road.'
    },
    {
      icon: TrendingUp,
      title: 'Strengthen Application',
      description: 'Build a stronger trademark case with comprehensive clearance data.'
    },
    {
      icon: Award,
      title: 'Make Informed Decisions',
      description: 'Get expert insights to refine your brand strategy before filing.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: 'Fast Results',
      description: 'Receive your search report within 24-48 hours'
    },
    {
      icon: Users,
      title: 'Expert Analysis',
      description: 'Trademark attorneys review all findings'
    },
    {
      icon: CheckCircle,
      title: 'Comprehensive',
      description: 'Federal, state, and common law coverage'
    },
    {
      icon: Clock,
      title: 'Free Consultation',
      description: 'Discuss results with our specialists'
    }
  ];

  const benefits = [
    {
      title: 'USPTO Database Search',
      description: 'Search all registered and pending federal trademarks thoroughly.'
    },
    {
      title: 'State-Level Coverage',
      description: 'Check all 50 states for similar registered business names.'
    },
    {
      title: 'Common Law Search',
      description: 'Find unregistered marks that may still have legal protection.'
    },
    {
      title: 'Domain Name Check',
      description: 'Verify if your trademark is being used in domain names online.'
    },
    {
      title: 'Risk Assessment',
      description: 'Get expert analysis on likelihood of successful registration.'
    },
    {
      title: 'Strategic Recommendations',
      description: 'Receive guidance on how to proceed with your trademark.'
    }
  ];

  const faqs = [
    {
      id: 1,
      question: 'Why do I need a trademark search?',
      answer: 'A trademark search helps identify existing marks that could conflict with yours, reducing the risk of application rejection and potential legal issues. It can save you thousands in rebranding costs.'
    },
    {
      id: 2,
      question: 'What does a comprehensive search include?',
      answer: 'Our comprehensive search includes federal USPTO records, all 50 state registrations, common law trademarks, domain names, and corporate directories to give you complete coverage.'
    },
    {
      id: 3,
      question: 'How long does the search take?',
      answer: 'We typically deliver your detailed search report within 24-48 hours. Rush services are available if you need results faster.'
    },
    {
      id: 4,
      question: 'What if conflicts are found?',
      answer: 'We provide a detailed analysis of any conflicts found, assess the risk level, and offer strategic recommendations on whether to proceed, modify your mark, or choose an alternative name.'
    },
    {
      id: 5,
      question: 'Is a search guaranteed to prevent rejection?',
      answer: 'While no search can guarantee approval, a comprehensive search significantly reduces your risk by identifying potential conflicts before filing your application.'
    },
    {
      id: 6,
      question: 'Can I search for logos or designs?',
      answer: 'Yes, we can search for word marks, logos, designs, and combined marks. Provide us with your mark details and we will conduct the appropriate search.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Trademark Search Services | Trademark Filing Solutions"
        description="Conduct a detailed trademark search before filing your application. Trademark Filing Solutions helps identify potential conflicts and improve approval chances."
      />
      {/* Banner */}
      <section className="py-12 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">Comprehensive Trademark Search</h1>
        </div>
      </section>

      {/* 1. About the Service */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Trademark Search
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-6">
                Avoid conflicts and rejections with a detailed, professional trademark availability check. Our comprehensive search covers federal, state, and common law databases to ensure your brand is clear.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Starting at just $149, get a thorough trademark clearance report with expert analysis and strategic recommendations in 24-48 hours.
              </p>
              <PopupButton size="lg" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white">
                Get Started Now
              </PopupButton>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-xl flex items-center justify-center bg-gradient-to-br from-blue-50 to-white p-4">
                <img 
                  src="https://stories.freepiklabs.com/storage/35149/Data-Extraction_Artboard-1-copy.svg" 
                  alt="Trademark Search Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Fast and thorough trademark clearance process</p>
          </div>
          <HowItWorksSlider items={processSteps} />
        </div>
      </section>

      {/* 3. Why You Need It */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why You Need a Trademark Search</h2>
            <p className="text-lg text-gray-600">Protect your investment before filing</p>
          </div>
          <WhyYouNeedItSlider items={whyYouNeedIt} />
        </div>
      </section>

      {/* 4. Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Search Package</h2>
            <p className="text-lg text-gray-600">Comprehensive coverage at every level</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {trademarkSearchPricing.map((plan) => (
              <Card key={plan.id} className={`relative flex flex-col h-full hover:shadow-2xl transition-all duration-300 ${plan.recommended ? 'ring-2 ring-cyan-600 scale-105' : ''}`}>
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white">
                    Select Plan
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Trademark Filing Solutions</h2>
            <p className="text-lg text-gray-600">Professional trademark search experts</p>
          </div>
          <WhyChooseUsSlider items={whyChooseUs} />
        </div>
      </section>

      {/* 6. Benefits */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600">What's included in your search</p>
          </div>
          <BenefitsSlider benefits={benefits} />
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600">Trusted by businesses nationwide</p>
          </div>
          <TestimonialSlider testimonials={searchTestimonials} />
        </div>
      </section>

      {/* 8. FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about trademark search</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold hover:text-cyan-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Search Your Trademark?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Get your comprehensive trademark search report in 24-48 hours.
          </p>
          <div className="flex justify-center gap-4">
              <PopupButton size="lg" variant="secondary" className="bg-white text-cyan-600 hover:bg-gray-100">
                Get Started Now
              </PopupButton>
            <Link to="/contact-us">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrademarkSearch;
